import { useCookies } from "react-cookie";

export interface UserPermissions {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
  // Inventory-specific permissions
  canManageInventory: boolean;
  // Coupon-specific permissions
  canManageCoupons: boolean;
  userType: string;
  isOwner: boolean;
  isAdmin: boolean;
  isSales: boolean;
  isCustomer: boolean;
}

/**
 * Hook to get user permissions based on user type
 * - O: Owner (full access to everything)
 * - A: Admin (full access except user management)
 * - S: Sales Person (can only manage inventory, no other entities)
 * - C: Customer (no POS access)
 */
export const usePermissions = (): UserPermissions => {
  const [cookies] = useCookies();
  const auth = cookies.auth;
  
  if (!auth) {
    return {
      canView: false,
      canAdd: false,
      canEdit: false,
      canDelete: false,
      canManageUsers: false,
      canManageInventory: false,
      canManageCoupons: false,
      userType: '',
      isOwner: false,
      isAdmin: false,
      isSales: false,
      isCustomer: false,
    };
  }
  
  const userType = auth.user_type;
  
  switch (userType) {
    case 'O': // Owner - full access
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true,
        canManageUsers: true,
        canManageInventory: true,
        canManageCoupons: true,
        userType,
        isOwner: true,
        isAdmin: false,
        isSales: false,
        isCustomer: false,
      };
      
    case 'A': // Admin - full access except user management
      return {
        canView: true,
        canAdd: true,
        canEdit: true,
        canDelete: true,
        canManageUsers: false,
        canManageInventory: true,
        canManageCoupons: true,
        userType,
        isOwner: false,
        isAdmin: true,
        isSales: false,
        isCustomer: false,
      };
      
    case 'S': // Sales Person - can only manage inventory
      return {
        canView: true,
        canAdd: false, // Cannot add other entities (brands, products, shops, etc.)
        canEdit: false, // Cannot edit other entities
        canDelete: false, // Cannot delete other entities
        canManageUsers: false, // Cannot manage users
        canManageInventory: true, // Can manage inventory only
        canManageCoupons: false, // Cannot manage coupons
        userType,
        isOwner: false,
        isAdmin: false,
        isSales: true,
        isCustomer: false,
      };
      
    case 'C': // Customer - no POS access
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canManageUsers: false,
        canManageInventory: false,
        canManageCoupons: false,
        userType,
        isOwner: false,
        isAdmin: false,
        isSales: false,
        isCustomer: true,
      };
      
    default:
      return {
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canManageUsers: false,
        canManageInventory: false,
        canManageCoupons: false,
        userType,
        isOwner: false,
        isAdmin: false,
        isSales: false,
        isCustomer: false,
      };
  }
};